import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Eye, 
  Smartphone, 
  Monitor, 
  Tablet,
  Globe,
  Clock,
  MapPin,
  RefreshCw,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScanHistoryPage = () => {
  const { cardId } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [scanHistory, setScanHistory] = useState({ total_views: 0, views: [] });

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [cardRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/cards/${cardId}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/cards/${cardId}/scan-history?limit=100`, { headers: getAuthHeaders() })
      ]);
      setCardData(cardRes.data);
      setScanHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to load scan history');
      navigate('/dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [cardId]);

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Smartphone className="w-4 h-4" />;
      case 'tablet':
        return <Tablet className="w-4 h-4" />;
      default:
        return <Monitor className="w-4 h-4" />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const exportToCSV = () => {
    if (scanHistory.views.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Create CSV header
    const headers = ['Date', 'Time', 'Device Type', 'Browser', 'OS', 'Location', 'IP (Masked)'];
    
    // Create CSV rows
    const rows = scanHistory.views.map(view => {
      const date = new Date(view.timestamp);
      return [
        date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
        date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        view.device_type || 'Unknown',
        view.browser || 'Unknown',
        view.os || 'Unknown',
        view.location || 'Unknown',
        view.ip_masked || 'N/A'
      ];
    });

    // Combine header and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${cardData?.name || 'card'}_scan_history_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('CSV exported successfully');
  };

  const getDeviceColor = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return 'bg-blue-100 text-blue-700';
      case 'tablet':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/cards/${cardId}/edit`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">Card Views</h1>
            <p className="text-muted-foreground">See who viewed your card: {cardData?.name}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => fetchData(true)} 
            disabled={refreshing}
            className="gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="default" 
            onClick={exportToCSV}
            disabled={scanHistory.views.length === 0}
            className="gap-2 bg-[#16335E] hover:bg-[#0A1A34]"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#16335E]/10 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-[#16335E]" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                  <p className="text-3xl font-bold">{cardData?.scan_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Mobile Views</p>
                  <p className="text-3xl font-bold">
                    {scanHistory.views.filter(v => v.device_type?.toLowerCase() === 'mobile').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Monitor className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Desktop Views</p>
                  <p className="text-3xl font-bold">
                    {scanHistory.views.filter(v => v.device_type?.toLowerCase() === 'desktop').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* View History */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Views</CardTitle>
            <CardDescription>
              Showing the last {scanHistory.views.length} views of your card
            </CardDescription>
          </CardHeader>
          <CardContent>
            {scanHistory.views.length === 0 ? (
              <div className="text-center py-12">
                <Eye className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No views yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Share your card to start tracking views
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {scanHistory.views.map((view, index) => (
                  <div 
                    key={view.id || index}
                    className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    data-testid={`view-item-${index}`}
                  >
                    {/* Device Icon */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getDeviceColor(view.device_type)}`}>
                      {getDeviceIcon(view.device_type)}
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{view.device_type || 'Unknown'}</span>
                        {view.browser && view.browser !== 'Unknown' && (
                          <Badge variant="outline" className="text-xs">
                            {view.browser}
                          </Badge>
                        )}
                        {view.os && view.os !== 'Unknown' && (
                          <Badge variant="outline" className="text-xs">
                            {view.os}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        {view.location && view.location !== 'Unknown' && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {view.location}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Timestamp */}
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTimestamp(view.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ScanHistoryPage;
